// ============================================================================
// UNIVERSAL ACTION HANDLERS - ENTITY HISTORY & AUDIT
// ============================================================================
//
// Registering "View history & audit" as an ordinary action (here + in the
// entity's `detailActions`) is the WHOLE implementation of the entry point:
// ENTITY_TABLE_ROW_ACTIONS guarantees every canonical action appears in BOTH
// required affordances automatically — the visible `⋯` button
// (`Unified<Entity>RowActions`) AND right-click (`Unified<Entity>RowContextMenu`)
// — on every surface that renders a contact or company. Hand-adding a
// context-menu entry instead would bypass this map, the Action Resolver, and
// RBAC, and would only appear on the one surface it was added to.
//
// The modal is a PLACE: it owns a browser history entry. `onOpenModal` puts it
// on the modal stack synchronously (up on the same tick as the click) and
// `EntityAuditModal` then claims the history entry through
// `useHubModalController` — see the note at the top of that component for why
// the push happens there rather than here.

import {
  AUDIT_HISTORY_MODALS,
  isAuditHistoryEntityType,
} from "@/components/audit/audit-modal-config";

import type { UniversalActionHandlers } from "./types";

/** The record's own name, for the modal title. Never put in the URL. */
function readEntityLabel(item: Record<string, unknown>): string | undefined {
  for (const key of ["name", "company_name", "title"]) {
    const value = item[key];
    if (typeof value === "string" && value.trim().length > 0) return value;
  }
  return undefined;
}

export const auditActions: UniversalActionHandlers = {
  viewAuditHistory: async (item, context) => {
    const entityName = context.entityName;
    if (!isAuditHistoryEntityType(entityName)) {
      context.onToast("History is not tracked for this record type yet", "info");
      return;
    }

    const rawId: unknown = item?.id;
    if (typeof rawId !== "string" || rawId.length === 0) {
      context.onToast("No record ID available", "warning");
      return;
    }

    const descriptor = AUDIT_HISTORY_MODALS[entityName];
    context.onOpenModal(descriptor.modalId, {
      [descriptor.idParams[0]]: rawId,
      entityLabel: readEntityLabel(item),
    });
  },
};
